import type { AdminContactListRow } from '@oracord/shared';

export type ContactFollowUpFilter = 'all' | 'open' | 'none';
export type ContactDirectorySort = 'default' | 'newest' | 'oldest' | 'open-followups' | 'calls-desc' | 'name';

export type ContactDirectoryFilters = {
  search: string;
  followUps: ContactFollowUpFilter;
  sort: ContactDirectorySort;
};

export type ContactDirectorySummary = {
  total: number;
  visible: number;
  withOpenFollowUps: number;
  unknownContacts: number;
};

export function contactSearchText(row: Pick<AdminContactListRow, 'phone' | 'name' | 'company'>): string {
  return [row.phone, row.name, row.company]
    .filter(Boolean)
    .join(' ');
}

export function contactMatchesSearch(
  row: Pick<AdminContactListRow, 'phone' | 'name' | 'company'>,
  search: string,
): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return contactSearchText(row).toLowerCase().includes(needle);
}

function contactTime(row: Pick<AdminContactListRow, 'lastContactAt'>): number {
  if (!row.lastContactAt) return 0;
  const value = new Date(row.lastContactAt).getTime();
  return Number.isNaN(value) ? 0 : value;
}

function contactName(row: Pick<AdminContactListRow, 'name' | 'company' | 'phone'>): string {
  return (row.name || row.company || row.phone).toLowerCase();
}

export function sortContactsForDirectory<T extends Pick<AdminContactListRow, 'lastContactAt' | 'pendingFollowUps' | 'totalCalls' | 'name' | 'company' | 'phone'>>(
  rows: T[],
  sort: ContactDirectorySort,
): T[] {
  if (sort === 'default') return rows;

  return [...rows].sort((a, b) => {
    if (sort === 'open-followups') {
      const followUpDelta = b.pendingFollowUps - a.pendingFollowUps;
      if (followUpDelta !== 0) return followUpDelta;
      return contactTime(b) - contactTime(a);
    }
    if (sort === 'calls-desc') {
      const callDelta = b.totalCalls - a.totalCalls;
      if (callDelta !== 0) return callDelta;
      return contactTime(b) - contactTime(a);
    }
    if (sort === 'name') {
      return contactName(a).localeCompare(contactName(b));
    }

    const delta = contactTime(a) - contactTime(b);
    return sort === 'oldest' ? delta : -delta;
  });
}

function matchesFollowUpFilter(
  row: Pick<AdminContactListRow, 'pendingFollowUps'>,
  followUps: ContactFollowUpFilter,
): boolean {
  if (followUps === 'open') return row.pendingFollowUps > 0;
  if (followUps === 'none') return row.pendingFollowUps === 0;
  return true;
}

export function filterContactsForDirectory<T extends AdminContactListRow>(
  rows: T[],
  filters: ContactDirectoryFilters,
): T[] {
  const filtered = rows.filter(row => (
    contactMatchesSearch(row, filters.search) &&
    matchesFollowUpFilter(row, filters.followUps)
  ));
  return sortContactsForDirectory(filtered, filters.sort);
}

export function summarizeContactsDirectory<T extends Pick<AdminContactListRow, 'pendingFollowUps' | 'name'>>(
  rows: T[],
  visible: number,
): ContactDirectorySummary {
  return {
    total: rows.length,
    visible,
    withOpenFollowUps: rows.filter(row => row.pendingFollowUps > 0).length,
    unknownContacts: rows.filter(row => row.name === null).length,
  };
}
